/**
 * `LivelloSelect` — selettore controllato del **Livello** (`e-1..e-5`).
 *
 * Design ref: "Componenti chiave" → un piccolo componente `LivelloSelect`
 * riutilizzabile centralizza rendering ed etichette del Livello, usato accanto
 * allo `StarRating` nell'autovalutazione (task 9.2) e nell'editor Competenza.
 * *Validates: Requirements 1.2, 1.3.*
 *
 * La scala Livello (`LIVELLO_VALUES`, tipo `Livello`, `LIVELLO_LABELS`) è
 * definita in `shared/dto` e importata da lì: il componente NON ridefinisce la
 * scala né duplica le etichette.
 *
 * Il componente è **controllato** e rappresenta esplicitamente lo stato
 * "non selezionato" con il valore `null`, così da supportare l'obbligatorietà
 * lato autovalutazione (lo User deve scegliere un Livello prima di salvare,
 * Req. 2.5). Quando `value` è `null` viene mostrata un'opzione placeholder non
 * selezionabile una volta scelto un valore.
 *
 * In alcuni contesti (es. editor Competenza) il "nessun livello" è invece uno
 * stato finale valido e riselezionabile: passando `onClear` l'opzione
 * placeholder diventa selezionabile e la sua scelta notifica l'azzeramento del
 * Livello. Senza `onClear` il comportamento resta quello obbligatorio.
 */
import { useId } from 'react';
import { LIVELLO_LABELS, LIVELLO_VALUES, isLivello, type Livello } from '../shared/dto';

export interface LivelloSelectProps {
  /** Livello corrente, oppure `null` se non ancora selezionato. */
  value: Livello | null;
  /** Notifica il Livello scelto dall'utente (sempre un valore valido). */
  onChange: (value: Livello) => void;
  /**
   * Se fornito, l'opzione placeholder diventa **selezionabile** e la sua scelta
   * invoca questo callback per azzerare il Livello (stato "nessun livello").
   * Assente ⇒ il placeholder resta non selezionabile (Livello obbligatorio).
   */
  onClear?: () => void;
  /** Etichetta accessibile del controllo (es. "Livello di React"). */
  label: string;
  /** Disabilita l'interazione (sola lettura / non modificabile). */
  disabled?: boolean;
  /** Testo dell'opzione placeholder mostrata quando `value` è `null`. */
  placeholder?: string;
  /** `id` opzionale per collegare una `<label>` esterna. */
  id?: string;
  /**
   * Classe extra sul `<select>`. Consente di adottare lo stile "pill" comune
   * (`user-admin__select`) quando il controllo è avvolto nel relativo wrapper.
   */
  className?: string;
}

/**
 * Select del Livello a 5 valori. Emette solo valori appartenenti alla scala;
 * la scelta del placeholder (valore vuoto) non produce alcun `onChange`.
 */
export function LivelloSelect({
  value,
  onChange,
  onClear,
  label,
  disabled = false,
  placeholder = 'Seleziona un livello…',
  id,
  className,
}: LivelloSelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  // Il "nessun livello" è selezionabile solo quando il genitore fornisce
  // `onClear` (es. editor Competenza); altrimenti il Livello è obbligatorio.
  const clearable = onClear !== undefined;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (isLivello(next)) {
      onChange(next);
      return;
    }
    // Placeholder (valore vuoto): valido solo se il campo è azzerabile.
    if (next === '' && clearable) onClear();
  };

  return (
    <select
      id={selectId}
      className={className ? `livello-select ${className}` : 'livello-select'}
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      data-empty={value === null || undefined}
      onChange={handleChange}
    >
      {/* Opzione placeholder: rappresenta lo stato "non selezionato" (`null`).
          Selezionabile solo quando il campo è azzerabile (`onClear`). */}
      <option value="" disabled={!clearable}>
        {placeholder}
      </option>
      {LIVELLO_VALUES.map((livello) => (
        <option key={livello} value={livello}>
          {LIVELLO_LABELS[livello]}
        </option>
      ))}
    </select>
  );
}

export default LivelloSelect;
